import { useState, useRef } from 'react';

import {
  EColorVariablesV2,
  EPaletteModes,
  ThemePack,
  PANEL_THEME_COLOR_CONTENT_MAP,
  hex,
  opacity,
} from '@/types/theme-packs.types';
import Button from '@ui/buttons/button/button';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';

type TThemePackColorVariantChangeProps = {
  variant: EColorVariablesV2;
  themeMode: EPaletteModes;
  themePack?: ThemePack;
  disabled?: boolean;
  selectTab: () => void;
  active: boolean;
  disableColorCopy?: boolean;
};

export default function ThemePackColorVariantButton({
  disabled,
  themeMode,
  themePack,
  variant,
  selectTab,
  active,
  disableColorCopy = false,
}: TThemePackColorVariantChangeProps) {
  const colorMeta = PANEL_THEME_COLOR_CONTENT_MAP[variant];
  const colorArray = themePack?.colors?.[themeMode]?.[colorMeta?.mappedV1Value];

  if (!themePack || !colorArray || !colorMeta) return null;

  return (
    <Container
      className="position-relative radius-100"
      height="32px"
      width="164px"
      transitionDuration="0.2s"
      background={active ? 'var(--gray-100)' : 'var(--gray-700)'}
    >
      <Button
        height="100%"
        borderRadius="100px"
        className="flex ai-center jc-space-between radius-100"
        width="100%"
        padding="2px"
        disabled={disabled}
        disabledOpacity={1}
        onClick={selectTab}
        style={{
          background: active ? 'var(--gray-100)' : undefined,
        }}
      >
        <Container
          className="flex jc-space-between width-100-percent padding-4 ai-center cursor-pointer no-select"
          background={active ? 'var(--gray-900)' : 'var(--gray-700)'}
          hoverBackground="var(--gray-800)"
          style={{ transition: 'background-color 0.2s ease-in-out' }}
          height={'28px'}
          width={'160px'}
          borderRadius="100px"
          inheritButtonHover
        >
          <div className="flex ai-center gap-4 pointer-events-none">
            <div className="square-20 radius-full" style={{ backgroundColor: colorArray[0] }}></div>
            <label className="C1">{colorMeta.title}</label>
          </div>

          {/*
            This button is a placeholder so we can create the exact space we need for the real button below.
            Why? Because we'd have a button inside a button, which is semantically incorrect, so we move the real button
            outside of this button and just absolutely position it where this "dummy" button lives
          */}
          <CopyButton disabled hidden colorArray={colorArray} />
        </Container>
      </Button>

      <div className="flex ai-center position-absolute height-100-percent top-0 bottom-0 right-8">
        <CopyButton disabled={disableColorCopy} colorArray={colorArray} active={active} />
      </div>
    </Container>
  );
}

const CopyButton = ({
  hidden,
  disabled,
  colorArray,
  active,
}: {
  hidden?: boolean;
  disabled?: boolean;
  colorArray?: [hex, opacity];
  active?: boolean;
}) => {
  const [showCopiedText, setShowCopiedText] = useState(false);
  const copyTimer = useRef<NodeJS.Timeout | null>(null);

  const handleCopyToClipboard = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!colorArray?.[0]) return;
    if (copyTimer.current) {
      clearTimeout(copyTimer.current);
    }
    navigator.clipboard.writeText(colorArray[0]);
    setShowCopiedText(true);
    copyTimer.current = setTimeout(() => {
      setShowCopiedText(false);
    }, 2000);
  };

  const Tag = hidden ? 'div' : Button;
  const buttonProps = hidden
    ? {}
    : {
        backgroundColor: showCopiedText ? 'var(--gray-700)' : 'transparent',
        hoverBackgroundColor: active ? 'var(--gray-700)' : 'var(--gray-800)',
        disabledOpacity: 1,
        clear: true,
      };

  return (
    <Tag
      className="radius-12 padding-v-2 padding-h-8"
      onClick={handleCopyToClipboard}
      disabled={disabled}
      inert={hidden}
      style={{ visibility: hidden ? 'hidden' : 'visible' }}
      {...buttonProps}
    >
      <TooltipPortal
        open={showCopiedText}
        tooltipContent={
          <Text color="var(--gray-true-dark-100)" className="C2">
            Copied
          </Text>
        }
        tailSize={0}
        animationType="slide-up"
        childrenWrapperClass="flex ai-center"
        verticalOffset={-4}
        styles={{ padding: '4px' }}
      >
        <Text color="var(--gray-500)" className="C1">
          #&nbsp;
        </Text>
        <Text color="var(--gray-100)" className="C1 text-uppercase">
          {colorArray?.[0]?.replace('#', '').substring(0, 6)}
        </Text>
      </TooltipPortal>
    </Tag>
  );
};
